import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AutoAwesomeRounded, PersonRemoveRounded } from "@mui/icons-material";

import { Popper, Table } from "@fragments";

import FiltersBar from "./components/FiltersBar";
import MemberCell from "./components/MemberCell";
import RoleCell from "./components/RoleCell";
import SubscriptionCell from "./components/SubscriptionCell";
import MemberSinceCell from "./components/MemberSinceCell";
import RowActions from "./components/RowActions";
import TableToolbar from "./components/TableToolbar";

import "./styles.scss";

export default function MembersListView({
  open,
  ModalComponent,
  onClose,
  loading,
  members,
  page,
  pages,
  records,
  filters,
  plans,
  plansLoading,
  communityId,
  handleSearch,
  toggleFilterValue,
  handlePageChange,
  clearFilters,
  onActivateSubscription,
  onChangeRole,
  onRemoveMember,
  onExport,
}) {
  const { t } = useTranslation("memberModals");
  const navigate = useNavigate();
  const rowMenuPopperRef = useRef(null);

  function handleViewMember(member) {
    if (!member?.user_id) return;
    onClose();
    navigate(`/user/${member.user_id}`);
  }

  function openRowMenu(member, event) {
    event.stopPropagation();
    const isOwner = member?.role === "owner";
    const rawSubs = member?.subscriptions ?? member?.subscription;
    const hasSubscriptions = Array.isArray(rawSubs)
      ? rawSubs.length > 0
      : !!rawSubs && Object.keys(rawSubs).length > 0;
    const canActivate = member?.role === "member" || hasSubscriptions;

    const options = [
      canActivate && {
        id: "activate-subscription",
        label: t("membersList.actions.activateSubscription"),
        icon: <AutoAwesomeRounded fontSize="small" />,
        onClick: () => onActivateSubscription?.(member),
      },
      canActivate && !isOwner && { divider: true },
      !isOwner && {
        id: "remove-member",
        label: t("membersList.removeMember.title"),
        icon: <PersonRemoveRounded fontSize="small" />,
        variant: "danger",
        onClick: () => onRemoveMember?.(member),
      },
    ].filter(Boolean);

    rowMenuPopperRef.current?.open(options, "bottom-end", event.currentTarget);
  }

  const columns = [
    {
      id: "member",
      title: t("membersList.columns.member"),
      minWidth: 240,
      allowReorder: false,
      customRender: (item) => <MemberCell member={item} />,
    },
    {
      id: "role",
      title: t("membersList.columns.role"),
      minWidth: 140,
      align: "center",
      customRender: (item) => (
        <RoleCell member={item} onChangeRole={onChangeRole} />
      ),
    },
    {
      id: "subscription",
      title: t("membersList.columns.subscription"),
      minWidth: 200,
      grow: 2,
      align: "center",
      customRender: (item) => <SubscriptionCell member={item} plans={plans} />,
    },
    {
      id: "joined",
      title: t("membersList.columns.joined"),
      minWidth: 140,
      align: "left",
      customRender: (item) => <MemberSinceCell member={item} />,
    },
  ];

  return (
    <ModalComponent
      variant="search"
      maxWidth={1180}
      open={open}
      handleClose={onClose}
      loading={loading}
      onSearch={handleSearch}
      searchPlaceholder={t("membersList.searchPlaceholder")}
      subHeader={
        <FiltersBar
          filters={filters}
          plans={plans}
          plansLoading={plansLoading}
          onToggle={toggleFilterValue}
          onClear={clearFilters}
        />
      }
      bodyComponent={
        <div className="members-list-table-wrapper">
          <TableToolbar
            records={records}
            loading={loading}
            communityId={communityId}
            onExport={onExport}
          />
          <Table
            id="members-list"
            columns={columns}
            data={members}
            loading={loading}
            loadingRows={6}
            draggable={false}
            fullWidth
            emptyMessage={t("membersList.emptyMessage")}
            emptySubtitle={t("membersList.emptySubtitle")}
            enablePagination
            page={page}
            pages={pages}
            records={records}
            onSelectPage={handlePageChange}
            onRowClick={handleViewMember}
            actionsComponent={(item) => (
              <RowActions
                member={item}
                onView={handleViewMember}
                onOpenMenu={openRowMenu}
              />
            )}
          />
          <Popper controlRef={rowMenuPopperRef} />
        </div>
      }
    />
  );
}
